import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PersonalDetailsTemplate } from '@pages/Signup/Pages/PersonalDetails/PersonalDetailsTemplate'
import { usePersonalDetailsGoBack } from '@pages/Signup/routes/usePersonalDetailsGoBack'

import { useEntitySettings } from '@context/EntityContext'
import { useLegalLinksContext } from '@context/LegalLinksContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { SignupFormValues, useSignup } from '@context/SignupContext'

import { TickmillCompaniesEnum, isTickmillPartnerType } from '@utils/companyName.utils'
import { dispatchEntitySelected } from '@utils/cookie.utils'
import { filterDuplicatesById } from '@utils/duplicates'

import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'
import { LeadMembersEnum } from '@model/CreateLead'
import { RegulatorDto, RegulatorOptionDto } from '@model/RegulatorOptionDto'

import { useFetchRegulators } from '@hooks/useFetchRegulators'
import { useGeolocation } from '@hooks/useGeolocation'

import { Loading } from '@shared/components/Loading/Loading'

import { PersonalDetailsStep2Form } from './PersonalDetailsStep2Form'

interface OuterProps {
  onSubmit(values: SignupFormValues): void
}

export const PersonalDetailsStep2Page: React.FC<OuterProps> = (props) => {
  const { onSubmit } = props
  const { signupData } = useSignup()
  const locale = useSessionLanguage()
  const regulator = useRegulator()
  const regulators = useFetchRegulators()
  const countryLocation = useFetchCountryLocation(regulators.data)
  const goBack = usePersonalDetailsGoBack()

  const [currentProductIds, setCurrentProductIds] = useState<number[]>(
    signupData.values?.productIds || []
  )

  const { documents, isLoading: isDocumentsLoading } = useLegalLinksContext({
    countryId: countryLocation.countrySelected,
    tickmillCompanyId: regulator?.tickmillCompanyId || signupData.values?.tickmillCompanyId,
    productIds: currentProductIds,
  })

  const handleRegulatorSelected = (regulatorValue: RegulatorDto | undefined) => {
    regulator.setRegulatorSelected(regulatorValue)
    dispatchEntitySelected(
      regulatorValue?.tickmillCompanyId || TickmillCompaniesEnum.GENERIC,
      locale,
      signupData.values?.email,
      signupData.values?.firstName
    )
  }

  const handleCountrySelected = (country: string | undefined) => {
    countryLocation.setCountrySelected(country)
  }

  const isLoading = [
    regulators.isLoading,
    isDocumentsLoading,
    countryLocation.isLoading,
    !countryLocation.isReady,
  ].some(Boolean)

  if (isLoading) {
    return (
      <PersonalDetailsTemplate step={2} goBack={goBack} reversed>
        <Loading showLoadingIcon isLoading />
      </PersonalDetailsTemplate>
    )
  }

  return (
    <PersonalDetailsStep2Form
      step={2}
      formValues={signupData.values}
      groupReferences={groupReferences(documents)}
      passedDocuments={documents
        .filter(filterDocumentsByClient)
        .filter(filterDuplicatesById)
        .sort((a, b) => a.order - b.order)}
      regulatorSelected={regulator.regulatorSelected}
      regulatorOptions={regulators.data}
      countryId={countryLocation.countrySelected}
      tickmillCompanyId={regulator.tickmillCompanyId}
      onSubmit={onSubmit}
      onCountrySelectedChange={handleCountrySelected}
      onProductIdsChange={setCurrentProductIds}
      onRegulatorSelectedChange={handleRegulatorSelected}
      goBack={goBack}
    />
  )
}

interface FetchCountryLocationProps {
  countrySelected: string | undefined
  setCountrySelected(value: string | undefined): void
  isLoading: boolean
  isReady: boolean
  isFailed: boolean
}

const useFetchCountryLocation = (
  regulators: RegulatorOptionDto[] = []
): FetchCountryLocationProps => {
  const searchParams = new URLSearchParams(window.location.search)
  const { signupData } = useSignup()
  const [params] = useSearchParams()

  const countryId =
    signupData.values?.countryId || searchParams.get(LeadMembersEnum.COUNTRY_ID) || ''

  const shouldFetchGeolocation = !countryId && regulators.length >= 1
  const {
    countryCodeIso3,
    isLoading: geolocationLoading,
    isFailed: geolocationFailed,
  } = useGeolocation(shouldFetchGeolocation)

  const [countrySelected, setCountrySelected] = useState<string | undefined>(countryId)
  const [isLoading, setLoading] = useState<boolean>(true)
  const [isFailed, setFailed] = useState<boolean>(false)

  const isReady = useMemo(() => {
    return (!isLoading && !!countrySelected) || isFailed
  }, [isLoading, countrySelected, isFailed])

  useEffect(() => {
    if (countryId) {
      setCountrySelected(countryId)
      setLoading(false)
      return
    }

    if (!shouldFetchGeolocation) {
      setLoading(false)
      return
    }

    if (geolocationFailed) {
      setFailed(true)
      setLoading(false)
      return
    }

    if (geolocationLoading) {
      setLoading(true)
      return
    }

    if (countryCodeIso3) {
      const getCountryByGeolocation = regulators.find((x) => x.country.id === countryCodeIso3)

      if (getCountryByGeolocation && !params.get('redir') && !params.get('source')) {
        setCountrySelected(getCountryByGeolocation.country.id)
      } else {
        setCountrySelected(regulators[0]?.country.id)
      }
    }

    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryId,
    countryCodeIso3,
    geolocationLoading,
    geolocationFailed,
    regulators,
    shouldFetchGeolocation,
  ])

  return { countrySelected, setCountrySelected, isLoading, isFailed, isReady }
}

interface RegulatorProps {
  tickmillCompanyId?: TickmillCompaniesEnum
  isPartnerType: boolean
  isRegulatorSelected: boolean
  regulatorSelected: RegulatorDto | undefined
  setRegulatorSelected(value: RegulatorDto | undefined): void
}

const useRegulator = (): RegulatorProps => {
  const [entity] = useEntitySettings()

  const [regulatorSelected, setRegulatorSelected] = useState<RegulatorDto | undefined>()

  const tickmillCompanyId = regulatorSelected?.tickmillCompanyId
  const isRegulatorSelected = !!tickmillCompanyId
  const isPartnerType = isTickmillPartnerType(entity.entity)

  if (isTickmillPartnerType(entity.entity)) {
    return {
      tickmillCompanyId: TickmillCompaniesEnum.TICKMILL_PA,
      isPartnerType,
      isRegulatorSelected: true,
      regulatorSelected,
      setRegulatorSelected,
    }
  }

  return {
    tickmillCompanyId,
    isPartnerType,
    isRegulatorSelected,
    regulatorSelected,
    setRegulatorSelected,
  }
}

const groupReferences = (documents: LegalDocumentDto[] = []): (string | null)[] => {
  const groupReferences = documents.map((x) => x.groupReference)

  return groupReferences.filter(function (item, pos) {
    return groupReferences.indexOf(item) === pos
  })
}

const filterDocumentsByClient = (document: LegalDocumentDto): boolean => {
  return document.step === 'lead'
}
